import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { customFetch, fetchCsrfToken } from '../utils/api';

interface User {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_staff?: boolean;
  groups?: string[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  setUser: (user: User | null) => void;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  loginAsGuest: () => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
  register: (userData: any) => Promise<{ success: boolean; message?: string }>;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // On initial load, fetch CSRF token and validate auth token if present
    const initialize = async () => {
      // Fetch CSRF token for Chrome cross-domain compatibility
      await fetchCsrfToken();

      // If we have a token, validate it and fetch user data
      if (token) {
        try {
          const response = await customFetch('/api/profile/', {
            method: 'GET',
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else {
            // Token is invalid or expired, clear it
            console.warn('Token validation failed, clearing authentication');
            setToken(null);
            setUser(null);
            localStorage.removeItem('token');
          }
        } catch (error) {
          console.error('Error validating token:', error);
          // Clear invalid token on error
          setToken(null);
          setUser(null);
          localStorage.removeItem('token');
        }
      }
      setIsLoading(false);
    };

    initialize();
  }, [token]);

  const login = useCallback((email: string, password: string) => {
    return new Promise<{ success: boolean; message?: string }>((resolve) => {
      const performLogin = async () => {
        setIsLoading(true);
        try {
          // Always fetch CSRF token before login for Chrome compatibility
          // This is critical for cross-domain requests where cookies may be blocked
          await fetchCsrfToken();

          const isGuest = email.trim().toLowerCase() === 'guest@example.com';
          const loginUrl = isGuest ? '/api/guest-login/' : '/api/login/';

          const response = await customFetch(loginUrl, {
            method: 'POST',
            body: JSON.stringify({ email: email.trim(), password }),
          });

          if (response.ok) {
            const data = await response.json();
            setToken(data.token);
            setUser(data.user);
            localStorage.setItem('token', data.token);
            resolve({ success: true });
          } else {
            const errorData = await response.json();
            resolve({ success: false, message: errorData.detail || 'Invalid credentials or server error.' });
          }
        } catch (error) {
          console.error('Login failed:', error);
          resolve({ success: false, message: 'An unexpected error occurred.' });
        } finally {
          setIsLoading(false);
        }
      };
      performLogin();
    });
  }, []);

  const loginAsGuest = useCallback(() => {
    return login('guest@example.com', 'gudu-guest-password');
  }, [login]);

  const logout = useCallback(async () => {
    const tokenToInvalidate = token; // Capture token before clearing state
    try {
      if (tokenToInvalidate) {
        await customFetch('/api/logout/', {
          method: 'POST',
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('token');
    }
  }, [token]);

  const register = useCallback(async (userData: Record<string, any>) => {
    setIsLoading(true);
    try {
      // Use the new custom registration endpoint that bypasses CSRF
      const response = await customFetch('/api/allauth-register/', {
        method: 'POST',
        body: JSON.stringify({
          email: userData.email?.trim() || '',
          password1: userData.password || '',
          password2: userData.password_confirm || '',
          first_name: userData.first_name?.trim() || '',
          last_name: userData.last_name?.trim() || '',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return { success: true, message: data.detail || 'Registration successful! Please check your email to verify your account.' };
      }
      
      // If the response is not OK, try to parse the error response
      let errorMessage = 'Registration failed.';
      
      try {
        const errorData = await response.json();
        
        // Handle custom API error format
        if (errorData.email) {
          errorMessage = `Email: ${errorData.email[0]}`;
        } else if (errorData.password1) {
          errorMessage = `Password: ${errorData.password1[0]}`;
        } else if (errorData.password2) {
          errorMessage = `Password confirmation: ${errorData.password2[0]}`;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      } catch (parseError) {
        // If response is not JSON (e.g., HTML error page), use status text
        errorMessage = `Registration failed: ${response.status} ${response.statusText}`;
      }

      return { success: false, message: errorMessage };
    } catch (error) {
      console.error('Registration failed:', error);
      return { success: false, message: 'An unexpected error occurred.' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value = useMemo(() => ({
    user, 
    token, 
    setUser,
    login, 
    loginAsGuest,
    logout, 
    register, 
    isLoading, 
    isAuthenticated: !!token && !!user 
  }), [user, token, isLoading, login, loginAsGuest, logout, register]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};